import { showError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { SimplePopup } from '@calendly/ui/components/popup'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useGetUserQuery } from 'common'
import { EventTypeSelectPopup } from 'components/event_type_select_popup'
import { SendFromCalendlyEmailModalProvider } from 'components/send_from_calendly'
import { trackShareAvailabilityEvent } from 'features/contacts'
import { useTrack } from 'hooks/analytics_events'
import { getContactDisplayName } from 'shared/utils/get_contact_display_name'

import { trackSendViaCalendlyEvent } from './analytics'
import { useShareAvailabilityMutation } from './api'


export const EventTypesShareProvider = ({ isFlowStarted, onFlowFinish, contact, source }) => {
  const [isEventTypesModalOpen, setIsEventTypesModalOpen] = useState(false)
  const [isAvailabilityEmailModalOpen, setIsAvailabilityEmailModalOpen] = useState(false)
  const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState(false)
  const [selectedEventType, setSelectedEventType] = useState(null)
  const [subject, setSubject] = useState(null)
  const [body, setBody] = useState(null)
  const [isNewSubmit, setIsNewSubmit] = useState(false)
  const [shareAvailability, { isLoading, isSuccess, isError }] = useShareAvailabilityMutation()
  const { data: userData } = useGetUserQuery()
  const track = useTrack()
  const contactDisplayName = getContactDisplayName(contact)

  const dispatch = useDispatch()

  const onModalClose = useCallback(() => {
    setSubject(null)
    setBody(null)
    setIsEventTypesModalOpen(false)
    setIsAvailabilityEmailModalOpen(false)
    setSelectedEventType(null)
    setIsConfirmationPopupOpen(false)
    setIsNewSubmit(false)
    onFlowFinish()
  }, [onFlowFinish])

  const onBackStep = () => {
    setIsAvailabilityEmailModalOpen(false)
    setSubject(null)
    setBody(null)
    setIsEventTypesModalOpen(true)
  }

  const onEventTypesModalConfirm = (item) => {
    setIsEventTypesModalOpen(false)
    setSelectedEventType(item)
    setSubject(item
      ? I18n.t('publisher.contacts.share.email_form.default_subject', { event_type_name: item.name })
      : I18n.t('publisher.contacts.share.email_form.default_subject_no_et'))
    setBody(item
      ? I18n.t('publisher.contacts.share.email_form.default_body', {
        event_type_link: item.booking_url,
        user_name: contactDisplayName,
        utm_content_event_type_link: `${item.booking_url}?utm_content=share_availability`
      })
      : I18n.t('publisher.contacts.share.email_form.default_body_no_et', {
        user_name: contactDisplayName
      }))
    setIsAvailabilityEmailModalOpen(true)
    trackShareAvailabilityEvent({
      contactUuid: contact.uuid,
      source: null,
      step: 'select_et',
      track
    })
  }

  const onAvailabilityEmailModalClose = ({ subject: formSubject, body: formBody }) => {
    setSubject(formSubject)
    setBody(formBody)
    setIsAvailabilityEmailModalOpen(false)
    setIsConfirmationPopupOpen(true)
  }

  const onConfirmationPopupClose = () => {
    setIsConfirmationPopupOpen(false)
    setIsAvailabilityEmailModalOpen(true)
  }

  const onSubmitEmail = useCallback(({
    body: { html: submitBody },
    subject: { plainText: submitSubject },
    send_with: sendWith,
    include_cancellation_policy: includeCancellationPolicy
  }) => {
    shareAvailability(
      {
        body: submitBody,
        subject: submitSubject,
        to: contact.email,
        contact_uuid: contact.uuid,
        event_type_uuid: selectedEventType?.uuid,
        send_with: sendWith,
        include_cancellation_policy: includeCancellationPolicy
      })
    trackSendViaCalendlyEvent({ track, contactUuid: contact.uuid, step: 'send_email' })
    setIsNewSubmit(true)
  }, [contact?.email, contact?.uuid, selectedEventType?.uuid, shareAvailability, track])

  useEffect(() => {
    if (isSuccess && isNewSubmit && !isLoading) onModalClose()
  }, [isLoading, isNewSubmit, isSuccess, onModalClose])

  useEffect(() => {
    if (isError) dispatch(showError('Something went wrong'))
  }, [dispatch, isError])

  useEffect(() => {
    if (isFlowStarted) setIsEventTypesModalOpen(true)
  }, [isFlowStarted])

  return (
    <>
      <EventTypeSelectPopup
        isOpen={isEventTypesModalOpen}
        onCancel={onModalClose}
        contact={contact}
        onEventTypeSelect={onEventTypesModalConfirm}
        defaultValue={selectedEventType}
        actionKind="share_availability"
      />
      <SendFromCalendlyEmailModalProvider
        isOpen={isAvailabilityEmailModalOpen}
        onClose={onAvailabilityEmailModalClose}
        onConfirm={onSubmitEmail}
        onBack={onBackStep}
        isLoading={isLoading}
        initialSubject={subject}
        initialBody={body}
        title={contactDisplayName ? `${I18n.t('publisher.contacts.email')} ${contactDisplayName}` : I18n.t('publisher.contacts.email')}
        subTitle={`${I18n.t('publisher.booking_modal.sharing')} ${selectedEventType?.name}`}
        eventType={selectedEventType}
        userData={userData}
        contact={contact}
        source={source}
      />
      {isConfirmationPopupOpen && (
        <SimplePopup
          title={I18n.t('publisher.contacts.confirmation_popup.header')}
          onConfirm={onModalClose}
          onClose={onConfirmationPopupClose}
          cancelLabel={I18n.t('publisher.contacts.confirmation_popup.cancel')}
          confirmLabel={I18n.t('publisher.contacts.confirmation_popup.confirm')}
          size="medium"
        >
          {I18n.t('publisher.contacts.confirmation_popup.body.with_name', { name: contactDisplayName })}
        </SimplePopup>
      )}
    </>
  )
}
